import type { LigneVivier } from '../partage/api/types';
import { enFrancais } from '../partage/datesEnMots';
import { mesurerLAttente } from './mesureDeLAttente';
import { MOTS_ETAT } from '../partage/vocabulaire';
import { teinteDuProjet } from '../apparence';
import './LigneAttente.css';

/**
 * Une ligne de « À vérifier » — et c'est la seule voix qui se dessine au lieu de se dire.
 *
 * Le reste de l'accueil énonce ; ici on **montre le temps qui a passé**, parce que c'est
 * la seule chose qui compte et qu'une phrase la rend abstraite. La piste va du jour où la
 * demande est partie jusqu'à aujourd'hui ; le repère marque le jour où le monde devait
 * répondre. Ce qui dépasse le repère est l'attente en trop.
 *
 * L'ambre, jamais le rouge : ce qui attend le monde n'est pas un retard (principe 3).
 * On ne compte pas les jours de retard, on montre une longueur — et une longueur ne
 * reproche rien.
 *
 * **Deux attentes** (chantier priorites, ticket 03) : « À vérifier » montre toute l'attente du
 * monde. L'attente **en cours** (`situation.nom === 'en_attente'`) se lit « En cours » : la
 * jauge avance vers le repère, rien à relancer, seul « C'est arrivé ». L'attente **qui dure
 * trop** (`a_verifier`) garde son dessin : « Rien n'est revenu », la piste qui dépasse le
 * repère, « Relancer ».
 *
 * La longueur de la jauge se mesure dans `mesureDeLAttente.ts`, au jour de l'utilisateur.
 */
export function LigneAttente({
  ligne,
  aujourdhui,
  etiquette,
  onOuvrir,
  onArrive,
  onRelancer,
}: {
  ligne: LigneVivier;
  /** Le jour de l'utilisateur, « aaaa-mm-jj » : la jauge en cours compte jusqu'à lui. */
  aujourdhui: string;
  /** Qui parle — au pouce, les quatre voix tiennent dans une seule liste. */
  etiquette?: string;
  onOuvrir: (id: string) => void;
  onArrive: (id: string) => void;
  onRelancer: (id: string) => void;
}) {
  const dureTrop = ligne.situation.nom === 'a_verifier';
  const jauge = mesurerLAttente(ligne.dateAction, ligne.situation, aujourdhui);

  return (
    <article className={`attente${dureTrop ? '' : ' attente--en-cours'}`}>
      <svg
        className="attente-pastille"
        width="18"
        height="18"
        viewBox="0 0 19 19"
        fill="none"
        aria-label={MOTS_ETAT.en_attente_confirmation}
      >
        <circle
          cx="9.5"
          cy="9.5"
          r="8.7"
          stroke="var(--monde)"
          strokeWidth="1.4"
          strokeDasharray="2.6 3.2"
        />
      </svg>

      <div className="attente-corps">
        {etiquette && <p className="attente-voix">{etiquette}</p>}
        <div className="attente-tete">
          <button type="button" className="attente-titre" onClick={() => onOuvrir(ligne.id)}>
            {ligne.titre}
          </button>
          {ligne.dossier && (
            <>
              <span
                className="attente-point"
                style={{ ['--teinte' as string]: teinteDuProjet(ligne.dossier.couleur) }}
                aria-hidden="true"
              />
              <span className="attente-dossier">{ligne.dossier.titre}</span>
            </>
          )}
        </div>

        {jauge && (
          <div className="attente-jauge">
            <div className="attente-piste">
              <div className="attente-passe" style={{ width: `${jauge.part}%` }} />
              {/* En cours, le repère est au bout de la piste : la part est le temps déjà passé. */}
              <div className="attente-repere" style={{ left: `${dureTrop ? jauge.part : 100}%` }} />
            </div>
            <div className="attente-bornes">
              <span>Partie le {enFrancais(ligne.dateAction!)}</span>
              <span className="attente-attendue">
                Attendue le {enFrancais(ligne.situation.attenduLe!)}
              </span>
            </div>
          </div>
        )}

        {!jauge && !dureTrop && (
          <p className="attente-quand">
            {ligne.situation.attenduLe
              ? `Réponse attendue le ${enFrancais(ligne.situation.attenduLe)}`
              : 'Réponse attendue, sans délai connu'}
          </p>
        )}

        <div className="attente-gestes">
          <p>{dureTrop ? 'Rien n’est revenu.' : 'En cours.'}</p>
          <button type="button" className="attente-arrive" onClick={() => onArrive(ligne.id)}>
            <svg width="13" height="13" viewBox="0 0 15 15" fill="none" aria-hidden="true">
              <path d="M3 7.9l3.1 3.1L12.3 4.6" stroke="var(--monde-fonce)" strokeWidth="1.6" />
            </svg>
            C’est arrivé
          </button>
          {dureTrop && (
            <button
              type="button"
              className="attente-relancer"
              onClick={() => onRelancer(ligne.id)}
              title="L’attente repart d’aujourd’hui"
            >
              Relancer
            </button>
          )}
        </div>
      </div>
    </article>
  );
}

